import { ApplicationRef, inject, PLATFORM_ID, provideAppInitializer } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

/**
 * Renders a newly activated page in the same task that inserted it. `RouterOutlet` attaches the
 * page's DOM straight away but leaves its bindings to the next scheduled change detection, and
 * without zone.js that runs in a later task. In between the browser computes styles — on its own
 * after the click that navigated (hover update) or in a frame — for elements whose classes and
 * attributes are not bound yet, so every `transition` on them plays from the unstyled state.
 */
export const provideRouteRenderOnActivation = () =>
  provideAppInitializer(() => {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) {
      return;
    }

    const appRef = inject(ApplicationRef);
    const router = inject(Router);
    let active = new Set<ActivatedRoute>();

    router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      // The router keeps a route's `ActivatedRoute` when it reuses its page (new query or params)
      // and creates one when an outlet activates a page, so a new one is a newly inserted page.
      const next = new Set(routesOf(router.routerState.root));
      const activated = [...next].some((route) => !active.has(route));

      active = next;

      if (activated) {
        appRef.tick();
      }
    });
  });

function routesOf(route: ActivatedRoute): ActivatedRoute[] {
  return [route, ...route.children.flatMap(routesOf)];
}
